const MAX_EVENT_BYTES = 6000;
const MAX_BODY_BYTES = 2400;

const SENSITIVE_HEADER_NAMES = new Set([
  'authorization',
  'cookie',
  'set-cookie',
  'x-api-key',
  'proxy-authorization',
]);

const SENSITIVE_FIELD_PATTERN =
  /password|secret|token|credential|api[_-]?key|access[_-]?token|refresh[_-]?token/i;

const RUM_DATAPLANE_PATTERN = /dataplane\.rum\..+\.amazonaws\.com/i;

export const HTTP_CONTENT_EVENT_TYPE = 'com.lingk.rum.http_content';
export const CONSOLE_LOG_EVENT_TYPE = 'com.lingk.rum.console_log';

export const isRumDataplaneUrl = (url: string): boolean => RUM_DATAPLANE_PATTERN.test(url);

export const truncateToBytes = (value: string, maxBytes: number): string => {
  if (!value) return value;
  const encoder = new TextEncoder();
  if (encoder.encode(value).length <= maxBytes) return value;
  let low = 0;
  let high = value.length;
  while (low < high) {
    const mid = Math.ceil((low + high) / 2);
    if (encoder.encode(value.slice(0, mid)).length <= maxBytes) {
      low = mid;
    } else {
      high = mid - 1;
    }
  }
  return `${value.slice(0, low)}…[truncated]`;
};

const redactValue = (key: string, value: unknown): unknown => {
  if (SENSITIVE_FIELD_PATTERN.test(key)) {
    return '[redacted]';
  }
  if (Array.isArray(value)) {
    return value.map((item, index) => redactValue(String(index), item));
  }
  if (value && typeof value === 'object') {
    return redactObject(value as Record<string, unknown>);
  }
  return value;
};

export const redactObject = (value: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(value).map(([key, entryValue]) => [key, redactValue(key, entryValue)])
  );

export const sanitizeHeaders = (headers: Record<string, string>): Record<string, string> =>
  Object.fromEntries(
    Object.entries(headers).map(([key, headerValue]) => [
      key,
      SENSITIVE_HEADER_NAMES.has(key.toLowerCase()) ? '[redacted]' : headerValue,
    ])
  );

const serializeError = (value: Error): string =>
  JSON.stringify({
    name: value.name,
    message: value.message,
    stack: value.stack,
  });

export const serializeConsoleArgs = (args: unknown[]): string => {
  const serialized = args.map((arg) => {
    if (arg instanceof Error) {
      return serializeError(arg);
    }
    if (typeof arg === 'string') {
      return arg;
    }
    try {
      return JSON.stringify(arg);
    } catch {
      return String(arg);
    }
  });
  return truncateToBytes(serialized.join(' '), MAX_BODY_BYTES);
};

export const serializeBody = (body: string, contentType?: string | null): string => {
  if (!body) return '';

  const normalizedType = contentType?.toLowerCase() ?? '';
  if (
    normalizedType.includes('application/json') ||
    body.trim().startsWith('{') ||
    body.trim().startsWith('[')
  ) {
    try {
      const parsed = JSON.parse(body) as unknown;
      if (parsed && typeof parsed === 'object') {
        return truncateToBytes(
          JSON.stringify(redactObject(parsed as Record<string, unknown>)),
          MAX_BODY_BYTES
        );
      }
    } catch {
      // Fall through to plain-text truncation.
    }
  }

  return truncateToBytes(body, MAX_BODY_BYTES);
};

export const fitEventPayload = <T extends Record<string, unknown>>(payload: T): T => {
  let serialized = JSON.stringify(payload);
  if (new TextEncoder().encode(serialized).length <= MAX_EVENT_BYTES) {
    return payload;
  }

  const fitted = { ...payload };
  if ('requestBody' in fitted && typeof fitted.requestBody === 'string') {
    fitted.requestBody = truncateToBytes(fitted.requestBody, MAX_BODY_BYTES / 2);
  }
  if ('responseBody' in fitted && typeof fitted.responseBody === 'string') {
    fitted.responseBody = truncateToBytes(fitted.responseBody, MAX_BODY_BYTES / 2);
  }
  if ('message' in fitted && typeof fitted.message === 'string') {
    fitted.message = truncateToBytes(fitted.message, MAX_BODY_BYTES);
  }

  serialized = JSON.stringify(fitted);
  if (new TextEncoder().encode(serialized).length <= MAX_EVENT_BYTES) {
    return fitted as T;
  }

  return {
    ...fitted,
    requestBody: '[truncated]',
    responseBody: '[truncated]',
    message:
      typeof fitted.message === 'string' ? truncateToBytes(fitted.message, 512) : fitted.message,
  } as T;
};
